import { supabase } from '../lib/supabase';

/**
 * Public, read-only access to published legal documents (Terms of Use,
 * Privacy Notice, Electronic Notice consent). Anonymous visitors read only
 * through the two SECURITY DEFINER RPCs; the legal_documents table itself is
 * never queried from the browser.
 */

/** Public page path for each document type. Single source for links elsewhere. */
export const LEGAL_DOC_PATHS = Object.freeze({
  terms: '/legal/terms',
  privacy: '/legal/privacy',
  electronic_notice: '/legal/electronic-notice',
});

/** URL slug (the :docSlug route segment) → doc_type. */
export const LEGAL_SLUG_TO_DOC_TYPE = Object.freeze(
  Object.fromEntries(
    Object.entries(LEGAL_DOC_PATHS).map(([docType, path]) => [path.replace('/legal/', ''), docType])
  )
);

/** Returns the doc_type for a route slug, or null when the slug is unknown. */
export const docTypeFromSlug = (slug) =>
  Object.prototype.hasOwnProperty.call(LEGAL_SLUG_TO_DOC_TYPE, slug)
    ? LEGAL_SLUG_TO_DOC_TYPE[slug]
    : null;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True for a canonical uuid string. */
export const isUuid = (value) => typeof value === 'string' && UUID_RE.test(value);

/**
 * Checkbox wording shown on the registration page. MUST equal REQUIRED_CONSENT_WORDING in
 * supabase/functions/_shared/legalConsent.ts, which redeem-invite stores on every
 * consent_records row (wording_version + wording_text). legalConsentWording.test.js
 * cross-checks the two files; change both together and bump the version id.
 * `parts` drive the rendering (the two document names are links); `text` is the plain
 * sentence a later reader sees in consent_records.wording_text.
 */
export const REQUIRED_CONSENT_WORDING = Object.freeze({
  version: 'required_checkbox_v1',
  parts: Object.freeze([
    'I agree to the ',
    Object.freeze({ docType: 'terms', text: 'Terms of Use' }),
    ' and have read the ',
    Object.freeze({ docType: 'privacy', text: 'Privacy Notice' }),
  ]),
  get text() {
    return this.parts.map((part) => (typeof part === 'string' ? part : part.text)).join('');
  },
});

/** Mirrors ELECTRONIC_NOTICE_WORDING_VERSION in supabase/functions/_shared/legalConsent.ts. */
export const ELECTRONIC_NOTICE_WORDING_VERSION = 'electronic_notice_checkbox_v1';

/**
 * Visible label text of the optional electronic-notice checkbox: the document's own
 * consent_label, a space, then a link whose text is the document title. Mirrors
 * electronicNoticeWordingText() in supabase/functions/_shared/legalConsent.ts (and the
 * consent_records_validate trigger), which is what redeem-invite records.
 */
export const electronicNoticeWordingText = (doc) => `${doc?.consent_label ?? ''} ${doc?.title ?? ''}`;

/**
 * The electronic-notice document from a preview `legal` block, only when it can be shown
 * exactly as the server records it (non-empty consent_label and title). No fallback text:
 * if this returns null the checkbox is not rendered and no choice is sent.
 */
export const offeredElectronicNotice = (legal) => {
  const optional = Array.isArray(legal?.optional) ? legal.optional : [];
  const doc = optional.find((d) => d?.doc_type === 'electronic_notice');
  return doc && typeof doc.consent_label === 'string' && doc.consent_label && typeof doc.title === 'string' && doc.title
    ? doc
    : null;
};

/**
 * Link to the public page for one document from a preview `legal` block, pinned to the
 * exact version the server returned for the invite's HOA:
 *   /legal/terms?v=<version>&h=<hoa_id>&d=<document_id>
 * Pins are added only when well-formed; with none it is the plain page path.
 */
export const legalDocHref = (docType, { version, hoaId, documentId } = {}) => {
  const path = LEGAL_DOC_PATHS[docType];
  if (!path) return null;
  const params = new URLSearchParams();
  if (Number.isInteger(version) && version > 0) params.set('v', String(version));
  if (isUuid(hoaId)) params.set('h', hoaId);
  if (isUuid(documentId)) params.set('d', documentId);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/** Pinned link for a document entry of a preview `legal` block (falls back to the plain path). */
export const legalBlockDocHref = (legal, docType) => {
  const list = [
    ...(Array.isArray(legal?.required) ? legal.required : []),
    ...(Array.isArray(legal?.optional) ? legal.optional : []),
  ];
  const doc = list.find((d) => d?.doc_type === docType);
  return legalDocHref(docType, {
    version: doc?.version,
    hoaId: legal?.hoa_id,
    documentId: doc?.document_id,
  });
};

/**
 * Reads the pin parameters of a /legal/* URL. Only well-formed uuids are honoured;
 * anything else is ignored (the page then uses the hostname's HOA / version lookup).
 */
export const parseLegalPinParams = (searchParams) => {
  const h = searchParams?.get ? searchParams.get('h') : null;
  const d = searchParams?.get ? searchParams.get('d') : null;
  return { hoaId: isUuid(h) ? h : null, documentId: isUuid(d) ? d : null };
};

/**
 * Parses the optional ?v= query value.
 * Returns { version: null } when absent, { version: n } for a positive integer,
 * and { invalid: true } for anything else.
 */
export const parseVersionParam = (raw) => {
  if (raw === null || raw === undefined || raw === '') return { version: null };
  if (!/^[1-9]\d{0,8}$/.test(String(raw))) return { invalid: true };
  return { version: Number.parseInt(raw, 10) };
};

const firstRow = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  return data || null;
};

/**
 * The published document of one type for an HOA: exactly `documentId` when given
 * (the server still requires the type, the HOA scope and, if set, the version to
 * match), else the current version when `version` is omitted, else that exact
 * published version. Resolves to { data: row | null, error }.
 */
export const getPublishedLegalDocument = async ({ hoaId, docType, version = null, documentId = null }) => {
  try {
    const { data, error } = await supabase.rpc('get_legal_document', {
      p_hoa_id: hoaId || null,
      p_doc_type: docType,
      p_version: version ?? null,
      ...(documentId ? { p_document_id: documentId } : {}),
    });
    if (error) return { data: null, error };
    return { data: firstRow(data), error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * The current published version of every document type for an HOA
 * (tenant override, else platform default). Resolves to { data: rows[], error }.
 */
export const getCurrentLegalDocuments = async (hoaId) => {
  try {
    const { data, error } = await supabase.rpc('get_current_legal_documents', {
      p_hoa_id: hoaId || null,
    });
    if (error) return { data: [], error };
    return { data: Array.isArray(data) ? data : [], error: null };
  } catch (error) {
    return { data: [], error };
  }
};
